// ===========================================================================
// ReHome Shelter — Auth Guards
// Requires react-router-dom (not in shared package).
// ===========================================================================

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@rehome/shared';
import { Loader2 } from 'lucide-react';

const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center py-20">
    <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
  </div>
);

/**
 * Route guard: only allows access to shelter users (user_type = 'shelter').
 * Redirects unauthorized users to /login.
 */
export const RequireShelterUser: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, userProfile, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  if (!user || !userProfile) {
    return <Navigate to="/login" replace />;
  }

  if (userProfile.user_type !== 'shelter') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/**
 * Route guard: redirects shelter users to /set-password
 * if password_change_required is true.
 */
export const RequirePasswordNotExpired: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userProfile, loading } = useAuth();

  if (loading) return <LoadingSpinner />;
  if (userProfile?.password_change_required) {
    return <Navigate to="/set-password" replace />;
  }

  return <>{children}</>;
};
